import type { Maybe } from '@adornis/base/utilTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { type FunctionalForm, useForm, validate } from '@adornis/forms/use-form.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useLocalStorage } from '@adornis/functional-lit/use-local-storage.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import type { Validator } from '@adornis/validation/validation.js';
import { AddressForm } from '@martinus/address/client/AddressForm.js';
import { Adresse } from '@martinus/address/entities/Adresse.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { PageFooter } from '@martinus/styles/client/PageFooter.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { openReportProblemDialog } from '../../../client/components/dialogs/ReportProblemDialog.js';
import type { Bundesland } from '../../../db/enums/misc-enums.js';
import { EinstellungsStatus } from '../../../db/enums/status.js';
import { School } from '../../../groups/entities/School.js';
import { getSchoolIDsForRoles } from '../../../permissions/api/getSchoolIDsForRoles.js';
import { EMPLOYEE_ROLES } from '../../../permissions/roles.js';
import { Person } from '../../entities/person.js';
import { BankDatenForm, BankDatenFormValidator } from '../../profile/client/BankDatenForm.js';
import { StammdatenForm, StammdatenFormValidator } from '../../profile/client/StammdatenForm.js';
import { BankDaten } from '../../profile/entities/BankDaten.js';
import { StammDaten } from '../../profile/entities/StammDaten.js';
import { ANAusbildung } from '../entities/employee-sections/an-ausbildung.js';
import { ANBeschaeftigung } from '../entities/employee-sections/an-beschaeftigung.js';
import { ANDokumente } from '../entities/employee-sections/an-dokumente.js';
import { ANEinverstaendnisErklaerungen } from '../entities/employee-sections/an-einverstaendniserklaerungen.js';
import { ANGeringfuegigeBeschaeftigung } from '../entities/employee-sections/an-geringfuegigebeschaeftigung.js';
import { ANInfos } from '../entities/employee-sections/an-infos.js';
import { ANPersonalakte } from '../entities/employee-sections/an-personalakte.js';
import { ANPrivatVersicherung } from '../entities/employee-sections/an-privatversicherung.js';
import { ANSchwerbehinderung } from '../entities/employee-sections/an-schwerbehinderung.js';
import { ANSozialVersicherung } from '../entities/employee-sections/an-sozialversicherung.js';
import { ANSteuerInfos } from '../entities/employee-sections/an-steuerinfos.js';
import { ANVermoegenswirksameLeistungen } from '../entities/employee-sections/an-vermoegenswirksameleistungen.js';
import { ANWorkingStudent } from '../entities/employee-sections/an-working-student.js';
import { sendEntryFormFinishedNotification } from '../sendEntryFormFinishedNotification.js';
import {
  EmployeeSectionAusbildungForm,
  EmployeeSectionAusbildungFormValidator,
} from './section-forms/EmployeeSectionAusbildungForm.js';
import {
  EmployeeSectionDokumenteForm,
  EmployeeSectionDokumenteFormValidator,
} from './section-forms/EmployeeSectionDokumenteForm.js';
import { EmployeeSectionDownloadsForm } from './section-forms/EmployeeSectionDownloadsForm.js';
import {
  EmployeeSectionEinverstaendniserklaerungenForm,
  EmployeeSectionEinverstaendniserklaerungenFormValidator,
} from './section-forms/EmployeeSectionEinverstaendniserklaerungenForm.js';
import {
  EmployeeSectionGeringfuegigeBeschaeftigungForm,
  EmployeeSectionGeringfuegigeBeschaeftigungFormValidator,
} from './section-forms/EmployeeSectionGeringfuegigeBeschaeftigungForm.js';
import {
  EmployeeSectionInfosForm,
  EmployeeSectionInfosFormValidator,
} from './section-forms/EmployeeSectionInfosForm.js';
import {
  EmployeeSectionPrivatVersicherungForm,
  EmployeeSectionPrivatVersicherungFormValidator,
} from './section-forms/EmployeeSectionPrivatVersicherungForm.js';
import {
  EmployeeSectionSchwerbehinderungForm,
  EmployeeSectionSchwerbehinderungFormValidator,
} from './section-forms/EmployeeSectionSchwerbehinderungForm.js';
import {
  EmployeeSectionSozialVersicherungForm,
  EmployeeSectionSozialVersicherungFormValidator,
} from './section-forms/EmployeeSectionSozialVersicherungForm.js';
import {
  EmployeeSectionSteuernForm,
  EmployeeSectionSteuernFormValidator,
} from './section-forms/EmployeeSectionSteuernForm.js';
import {
  EmployeeSectionVermoegenswirksameLeistungenForm,
  EmployeeSectionVermoegenswirksameLeistungenFormValidator,
} from './section-forms/EmployeeSectionVermoegenswirksameLeistungenForm.js';
import {
  EmployeeSectionWorkingStudentForm,
  EmployeeSectionWorkingStudentFormValidator,
} from './section-forms/EmployeeSectionWorkingStudentForm.js';

interface PageDefinitions {
  title: string;
  formComponent: (form: FunctionalForm<EntityData<Person>>) => Renderable;
  /**
   * Used to initalize an empty section
   */
  ClassType?: typeof AdornisEntity;
  pathOnPerson?: string[];
  /**
   * Optional validator for the page
   */
  validator?: (bundesland?: Bundesland) => Validator;
}

enum FormState {
  NOT_VISITED = 0,
  ERROR = 1,
  CORRECT = 2,
}

const pages: PageDefinitions[] = [
  {
    title: 'Stammdaten',
    formComponent: form => StammdatenForm({ form }),
    ClassType: StammDaten,
    pathOnPerson: ['profilDaten', 'stammDaten'],
    validator: () => ({ profilDaten: { stammdaten: StammdatenFormValidator({ konfession: true, religion: false }) } }),
  },
  {
    title: 'Adresse',
    formComponent: form =>
      AddressForm({
        form,
        streetAndNumberFieldPath: ['profilDaten', 'adresse', 'streetAndNumber'],
        zipFieldPath: ['profilDaten', 'adresse', 'zip'],
        cityFieldPath: ['profilDaten', 'adresse', 'city'],
        countryFieldPath: ['profilDaten', 'adresse', 'country'],
        stateFieldPath: ['profilDaten', 'adresse', 'state'],
        attentionLineFieldPath: ['profilDaten', 'adresse', 'attentionLine'],
        auslandsPlzFieldPath: ['profilDaten', 'adresse', 'auslandsPlz'],
        districtFieldPath: ['profilDaten', 'adresse', 'district'],
      }),
    ClassType: Adresse,
    pathOnPerson: ['profilDaten', 'adresse'],
    validator: () => ({
      profilDaten: {
        adresse: {
          streetAndNumber: sgNonOptional(),
          zip: sgNonOptional(),
          city: sgNonOptional(),
          country: sgNonOptional(),
        },
      },
    }),
  },
  {
    title: 'Bankinformationen',
    formComponent: form => BankDatenForm({ form }),
    ClassType: BankDaten,
    pathOnPerson: ['profilDaten', 'bankDaten'],
    validator: () => ({ profilDaten: { bankInfos: BankDatenFormValidator } }),
  },
  {
    title: 'Allgemeine Mitarbeiterdaten',
    formComponent: form => EmployeeSectionInfosForm({ form }),
    ClassType: ANInfos,
    pathOnPerson: ['mitarbeiterDaten', 'anInfos'],
    validator: () => ({ mitarbeiterDaten: { anInfos: EmployeeSectionInfosFormValidator } }),
  },
  {
    title: 'Steuerinformationen',
    formComponent: form => EmployeeSectionSteuernForm({ form }),
    ClassType: ANSteuerInfos,
    pathOnPerson: ['mitarbeiterDaten', 'anSteuerInfos'],
    validator: () => ({ mitarbeiterDaten: { anSteuerInfos: EmployeeSectionSteuernFormValidator } }),
  },
  {
    title: 'Sozialversicherung',
    formComponent: form => EmployeeSectionSozialVersicherungForm({ form }),
    ClassType: ANSozialVersicherung,
    pathOnPerson: ['mitarbeiterDaten', 'anSozialVersicherung'],
    validator: () => ({ mitarbeiterDaten: { anSozialVersicherung: EmployeeSectionSozialVersicherungFormValidator } }),
  },

  {
    title: 'Privatversicherung',
    formComponent: form => EmployeeSectionPrivatVersicherungForm({ form }),
    ClassType: ANPrivatVersicherung,
    pathOnPerson: ['mitarbeiterDaten', 'anPrivatVersicherung'],
    validator: () => ({ mitarbeiterDaten: { anPrivatVersicherung: EmployeeSectionPrivatVersicherungFormValidator } }),
  },
  {
    title: 'Ausbildung',
    formComponent: form => EmployeeSectionAusbildungForm({ form }),
    ClassType: ANAusbildung,
    pathOnPerson: ['mitarbeiterDaten', 'anAusbildung'],
    validator: () => ({ mitarbeiterDaten: { anAusbildung: EmployeeSectionAusbildungFormValidator } }),
  },
  {
    title: 'Werksstudent',
    formComponent: form => EmployeeSectionWorkingStudentForm({ form }),
    ClassType: ANWorkingStudent,
    pathOnPerson: ['mitarbeiterDaten', 'anWorkingStudent'],
    validator: () => ({ mitarbeiterDaten: { anWorkingStudent: EmployeeSectionWorkingStudentFormValidator } }),
  },
  {
    title: 'Geringfügige Beschäftigung',
    formComponent: form => EmployeeSectionGeringfuegigeBeschaeftigungForm({ form }),
    ClassType: ANGeringfuegigeBeschaeftigung,
    pathOnPerson: ['mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung'],
    validator: () => ({
      mitarbeiterDaten: { anGeringfuegigeBeschaeftigung: EmployeeSectionGeringfuegigeBeschaeftigungFormValidator },
    }),
  },
  {
    title: 'Schwerbehinderung',
    formComponent: form => EmployeeSectionSchwerbehinderungForm({ form }),
    ClassType: ANSchwerbehinderung,
    pathOnPerson: ['mitarbeiterDaten', 'anSchwerbehinderung'],
    validator: () => ({ mitarbeiterDaten: { anSchwerbehinderung: EmployeeSectionSchwerbehinderungFormValidator } }),
  },
  {
    title: 'Vermögenswirksame Leistungen',
    formComponent: form => EmployeeSectionVermoegenswirksameLeistungenForm({ form }),
    ClassType: ANVermoegenswirksameLeistungen,
    pathOnPerson: ['mitarbeiterDaten', 'anVermoegenswirksameLeistungen'],
    validator: () => ({
      mitarbeiterDaten: { anVermoegenswirksameLeistungen: EmployeeSectionVermoegenswirksameLeistungenFormValidator },
    }),
  },
  {
    title: 'Dokumente',
    formComponent: form => EmployeeSectionDokumenteForm({ form }),
    ClassType: ANDokumente,
    pathOnPerson: ['mitarbeiterDaten', 'anDokumente'],
    validator: bundesland => ({ mitarbeiterDaten: { anDokumente: EmployeeSectionDokumenteFormValidator(bundesland) } }),
  },
  { title: 'Informationen zum downloaden', formComponent: () => EmployeeSectionDownloadsForm() },
  {
    title: 'Einverständniserklärung',
    formComponent: form => EmployeeSectionEinverstaendniserklaerungenForm({ form }),
    ClassType: ANEinverstaendnisErklaerungen,
    pathOnPerson: ['mitarbeiterDaten', 'anEinverstaendnisErklaerungen'],
    validator: () => ({
      mitarbeiterDaten: { anEinverstaendnisErklaerungen: EmployeeSectionEinverstaendniserklaerungenFormValidator },
    }),
  },
];

export const SGEmployeeEntryForm = stateful((props: { personID: Maybe<string> }) => {
  // use the localstorage to get the last page the user was on
  const [currentPage, setCurrentPage] = useLocalStorage('employee-entry-form-current-page', 0);

  const [formStepsCorrect, setFormStepsCorrect] = useLocalStorage(
    'employee-entry-form-steps-correct',
    pages.map(() => FormState.NOT_VISITED),
  );

  const setFormState = (index: number, state: FormState) => {
    const newFormStepsCorrect = [...formStepsCorrect];
    newFormStepsCorrect[index] = state;
    setFormStepsCorrect(newFormStepsCorrect);
  };

  /**
   * Date loading person and school
   */
  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        username: 1,
        email: 1,
        emailConfirmed: 1,
        lastConfirmationEmailSent: 1,
        debitorennummer: 1,
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        legacyDebitorNumbers: { debitorNumber: 1, scope: 1 },
        nameAbbreviation: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        profilDaten: {
          stammdaten: selectionSet(() => StammDaten, 1),
          adresse: selectionSet(() => Adresse, 1),
          bankInfos: selectionSet(() => BankDaten, 1),
        },
        mitarbeiterDaten: {
          personalNr: 1,
          einstellungsStatus: 1,
          entryFormCompletedAt: 1,
          altePersonalNr: 1,
          anInfos: selectionSet(() => ANInfos, 2),
          anSteuerInfos: selectionSet(() => ANSteuerInfos, 2),
          anSozialVersicherung: selectionSet(() => ANSozialVersicherung, 1),
          anAusbildung: selectionSet(() => ANAusbildung, 1),
          anPrivatVersicherung: selectionSet(() => ANPrivatVersicherung, 1),
          anGeringfuegigeBeschaeftigung: selectionSet(() => ANGeringfuegigeBeschaeftigung, 2),
          anSchwerbehinderung: selectionSet(() => ANSchwerbehinderung, 2),
          anDokumente: selectionSet(() => ANDokumente, 1),
          anPersonalakte: selectionSet(() => ANPersonalakte, 1),
          anEinverstaendnisErklaerungen: selectionSet(() => ANEinverstaendnisErklaerungen, 1),
          anBeschaeftigung: selectionSet(() => ANBeschaeftigung, 1),
          anVermoegenswirksameLeitungen: selectionSet(() => ANVermoegenswirksameLeistungen, 2),
          anWorkingStudent: selectionSet(() => ANWorkingStudent, 1),
        },
      }),
    [props.personID],
  );

  if (!person) return nothing;

  const { value: schoolIdsOfUser, loading: schoolIdsOfUserLoading } = useQuery(
    () => getSchoolIDsForRoles(person, EMPLOYEE_ROLES),
    [person],
  );

  const { value: school, loading: schoolLoading } = useQuery(
    () => subscribeByID(School, schoolIdsOfUser?.[0] ?? '')({ name: 1, bundesland: 1 }),
    [schoolIdsOfUser],
  );

  /**
   * If anything is loading we want to intercept the rendering with the loading here so we dont objectify the person
   */
  if (personLoading || schoolLoading || schoolIdsOfUserLoading)
    return PageLoader({ cardOptions: [{ elements: 2 }, { title: 'Lade Formular...', elements: 10 }] });

  /**
   * Build the form
   */
  const [userObject] = useState(person.toObject());
  const form = useForm(userObject, { validator: pages.at(currentPage)?.validator?.(school?.bundesland) ?? null }, [
    currentPage,
  ]);

  const handleSave = async () => {
    await form.uploadAllPendingFiles();

    // the person might have choosen "no children" in the form
    // so we need to remove the children from the entity
    if (form.document.mitarbeiterDaten?.anInfos && !form.document.mitarbeiterDaten.anInfos.hatKinder) {
      form.document.mitarbeiterDaten.anInfos.kinder = [];
    }

    const value = constructValue(form.document) as Person;
    await value.save();
  };

  /**
   *
   * @returns true if no errors are found, false otherwise
   */
  const validateEntireForm = async () => {
    const errorSections: string[] = [];
    const formStates = [...formStepsCorrect];

    let index = 0;
    for (const page of pages) {
      if (!page.validator) {
        formStates[index] = FormState.CORRECT;
        index++;
        continue;
      }
      const error = Object.values(await validate(page.validator(school?.bundesland ?? undefined), form.document));
      if (error.length) {
        errorSections.push(page.title);

        formStates[index] = FormState.ERROR;
      } else {
        formStates[index] = FormState.CORRECT;
      }
      index++;
    }

    setFormStepsCorrect(formStates);

    if (errorSections.length)
      await alertDialog(
        errorSections.length > 1
          ? `Es gibt noch Fehler in den Sektionen: ${errorSections.join(', ')}`
          : `Es gibt noch Fehler in der Sektion: ${errorSections[0]}`,
      );

    return errorSections.length === 0;
  };

  /**
   * Finishes the form, by validation i completly
   * @returns
   */
  const finishForm = async () => {
    const isValid = await validateEntireForm();
    if (!isValid) return;

    const wasNew = form.document.mitarbeiterDaten?.einstellungsStatus === EinstellungsStatus.NEU;

    if (form.document.mitarbeiterDaten?.einstellungsStatus === EinstellungsStatus.NEU) {
      form.document.mitarbeiterDaten.einstellungsStatus = EinstellungsStatus.AUSGEFUELLT;
    }

    await guardDialog(
      async () => {
        const firstSchool = schoolIdsOfUser?.at(0);
        if (!firstSchool) throw new Error('Schule konnte nicht festgelegt werden');
        if (!form.document.mitarbeiterDaten) throw new Error('Mitarbeiterdaten konnten nicht gefunden werden');

        if (wasNew) {
          form.document.mitarbeiterDaten.entryFormCompletedAt = new Date();
        }

        await handleSave();

        if (wasNew) {
          await sendEntryFormFinishedNotification(firstSchool, form.document._id)();
        }
      },
      {
        success: async () => {
          await alertDialog(
            'Ihre Daten wurden vollständig validiert und gespeichert. Sie werden nun zur Startseite weitergeleitet.',
          );
          location.href = '/';
        },
      },
    );
  };

  const handleStep = async (jumpTo: number) => {
    /**
     * Save regardless so the user does not loose progress
     */
    await handleSave();

    const error = await form.validate();

    const isFinishForm = jumpTo === pages.length;

    if (error) {
      setFormState(currentPage, FormState.ERROR);
      const continueToNext = await openValidationErrorDialog({ error, hardValidate: isFinishForm });

      if (!continueToNext) {
        return;
      }
    } else {
      setFormState(currentPage, FormState.CORRECT);
    }

    /**
     * If the user is on the last page and wants to finish the index will be page length (pages.length - 1) is the last object and +1 is length
     */

    if (isFinishForm) {
      await finishForm();
      return;
    }

    setCurrentPage(jumpTo);
  };

  useEffect(() => {
    addPageHeaderAdditional(
      0,
      html`
        <div class="w-full flex justify-center p-2">
          <ul class="steps w-[95%] overflow-hidden">
            ${repeat(
              pages,
              (page, index) => `${page.title}-${formStepsCorrect.at(index)}`,
              (page, idx) => html`
                <li
                  @click=${async () => {
                    if (idx === currentPage) return;

                    /**
                     * directly jump to page
                     */
                    handleStep(idx);
                  }}
                  class="step ${
                    formStepsCorrect[idx] === FormState.CORRECT
                      ? 'step-success'
                      : formStepsCorrect[idx] === FormState.ERROR
                        ? 'step-error'
                        : formStepsCorrect[idx] === FormState.NOT_VISITED && idx === currentPage
                          ? 'step-neutral'
                          : ''
                  } ${idx !== currentPage ? 'cursor-pointer' : ''} "
                >
                  ${
                    idx !== currentPage
                      ? idx + 1
                      : html`<span ${hoverTooltip('bottom', page.title)} class="step-icon tooltip">
                          <span class="icon-[material-symbols--home-outline-rounded] size-5"></span>
                        </span> `
                  }
                </li>
              `,
            )}
          </ul>
        </div>
      `,
    );
    return () => {
      removePageHeaderAdditional(0);
    };
  }, [formStepsCorrect, currentPage]);

  return html`
    ${PageHeader({ title: 'Eintrittsformular' })}

    <div class="card-container">
      <div class="card card-body">
        ${CardHeader({ title: pages[currentPage]?.title ?? '' })} ${pages.at(currentPage)?.formComponent(form)}
      </div>
      <div class="h-32"></div>
    </div>

    ${PageFooter({
      content: html`
        <button
          class="btn btn-sm btn-ghost items-center gap-2"
          @click=${() =>
            openReportProblemDialog(
              `Eintrittsformular, Schritt ${currentPage + 1}/${pages.length}: ${pages[currentPage]?.title ?? 'unbekannt'}`,
            )}
        >
          <span class="icon-[material-symbols--bug-report-outline]"></span>
          Problem melden
        </button>
        <div class="flex flex-row items-center gap-4">
          <button
            class="btn btn-xl btn-ghost items-center gap-2 justify-center"
            @click=${async () => {
              handleStep(currentPage - 1);
            }}
            ?disabled=${currentPage === 0}
          >
            <span class="icon-[material-symbols--chevron-left]"></span>
            Zurück
          </button>
          <button
            class="btn btn-primary btn-xl items-center gap-2"
            @click=${async () => {
              handleStep(currentPage + 1);
            }}
          >
            ${currentPage === pages.length - 1 ? 'Formular abschließen' : 'Weiter'}
            <span class="icon-[material-symbols--chevron-right]"></span>
          </button>
        </div>
      `,
    })}
  `;
});
